import React, { useCallback, useEffect, useRef, useState } from 'react';
import Drawer from '@mui/material/Drawer';
import Grid from '@mui/material/Grid';
import { collapsedDrawerWidth, drawerWidth } from '../../theme';
import logoIcon from '../../assets/rockyFinanceLogo.svg';
import logoText from '../../assets/rockyFinanceText.svg';
import Navigation from '../navigation/Navigation';
import { UsersSearchBox } from '../../components';
import { useAppSelector } from '../../redux/hooks';
import { userDataSelectors } from '../../redux/userData';
import { SCREEN_WIDTH } from '../../components/finKitAvatar/breakpoints';
import { throttle } from 'lodash';
import { CrossLinksMenu } from '@product-studio/rocky-components';
import {
  Box,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Tooltip,
} from '@mui/material';
import { useTheme } from '@emotion/react';
import ReportIssueModal from '../reportIssueModal/ReportIssueModal';
import MapsUgcOutlinedIcon from '@mui/icons-material/MapsUgcOutlined';
import { getAccountsList, getProjectsList } from '../../redux/accounts';
import { ProjectSearchDto } from '../../types';

const Sidebar: React.FC = () => {
  const theme = useTheme();
  const drawerRef = useRef<HTMLDivElement>(null);

  const usersList = useAppSelector(userDataSelectors.getUsersList);

  const accounts = useAppSelector(getAccountsList);
  const projects = useAppSelector(getProjectsList) as ProjectSearchDto[];
  const [isMenuOpen, setIsMenuOpen] = useState(true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleWindowResize = useCallback(
    throttle(() => {
      setIsMenuOpen(window.innerWidth > SCREEN_WIDTH.DESKTOP16);
    }, 10),
    [window, setIsMenuOpen],
  );

  useEffect(() => {
    window.addEventListener('resize', handleWindowResize);
    handleWindowResize();
    return () => {
      window.removeEventListener('resize', handleWindowResize);
    };
  }, [handleWindowResize]);

  const handleOpenClick = useCallback(() => {
    setIsMenuOpen(true);
  }, [setIsMenuOpen]);

  const toggleMenu = useCallback(() => {
    if (window.innerWidth <= SCREEN_WIDTH.DESKTOP16) setIsMenuOpen(!isMenuOpen);
  }, [setIsMenuOpen, isMenuOpen]);

  const handleInteractionOutside = useCallback(
    (event: MouseEvent | FocusEvent) => {
      if (window.innerWidth >= SCREEN_WIDTH.DESKTOP16) return;
      const drawerEl = drawerRef.current;
      const drawer = document.getElementById('drawer');
      const crossLink = document.getElementById('cross-link');
      const searchPopper = document.getElementById('search-popper');
      const target = event.target as Node;
      if (
        drawer?.contains(target) ||
        crossLink?.contains(target) ||
        drawerEl?.contains(target) ||
        searchPopper?.contains(target)
      )
        return;
      setIsMenuOpen(false);
    },
    [],
  );

  useEffect(() => {
    if (!isMenuOpen || window.innerWidth > SCREEN_WIDTH.DESKTOP16) return;

    const drawerEl = drawerRef.current;
    if (!drawerEl) return;

    document.addEventListener('mousedown', handleInteractionOutside);

    return () => {
      document.removeEventListener('mousedown', handleInteractionOutside);
    };
  }, [isMenuOpen, handleInteractionOutside]);

  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const handleToggleModal = useCallback(() => {
    setIsModalOpen(!isModalOpen);
  }, [isModalOpen, setIsModalOpen]);

  return (
    <Drawer
      variant="permanent"
      open={isMenuOpen}
      PaperProps={{ elevation: 1, id: 'drawer', ref: drawerRef }}
      sx={{
        width: isMenuOpen ? drawerWidth : `${collapsedDrawerWidth}px`,
        flexShrink: 0,
        whiteSpace: 'nowrap',
        boxSizing: 'border-box',
        transition: (theme) =>
          theme.transitions.create(['width'], {
            easing: theme.transitions.easing.easeInOut,
            duration: '0.25s',
          }),
        '& .MuiDrawer-paper': {
          width: isMenuOpen ? drawerWidth : `${collapsedDrawerWidth}px`,
          boxSizing: 'border-box',

          transition: (theme) =>
            theme.transitions.create(['width'], {
              easing: theme.transitions.easing.easeInOut,
              duration: '0.25s',
            }),

          border: 'none',
          borderRadius: 0,
          whiteSpace: 'nowrap',
          overflowX: 'hidden',
          overflowY: 'scroll',
          display: 'flex',
          justifyContent: 'space-between',
          flexDirection: 'column',

          '&::-webkit-scrollbar': {
            width: '4px',
          },
        },
      }}
    >
      <Box>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            paddingInline: 2,
            ...theme.mixins.toolbar,
          }}
        >
          <ListItemButton
            onClick={toggleMenu}
            disableRipple
            sx={{
              borderRadius: 1,
              justifyContent: 'flex-start',
              p: 0.6,
            }}
          >
            <img
              src={logoIcon}
              alt="Rocky Finance Icon"
              style={{
                height: 50,
                justifyContent: 'center',
              }}
            />

            <Box
              component="span"
              sx={{
                display: { xs: 'none', sm: 'inline' },
                mt: 0.5,
                opacity: isMenuOpen ? 1 : 0,
                width: isMenuOpen ? 'auto' : 0,
                transition: isMenuOpen
                  ? 'opacity 175ms ease-out 200ms, width 150ms ease-out'
                  : 'opacity 100ms ease-in, width 100ms ease-in',
              }}
            >
              <img
                src={logoText}
                alt="Rocky Finance Text"
                style={{ height: 38 }}
              />
            </Box>
          </ListItemButton>
          {isMenuOpen && (
            <span id="cross-link">
              <CrossLinksMenu />
            </span>
          )}
        </div>
        <Grid px={1} pb={0}>
          {!!(usersList?.length || accounts.length || projects.length) && (
            <Grid item xs={12}>
              <UsersSearchBox
                options={usersList || []}
                handleOpenClick={handleOpenClick}
                open={isMenuOpen}
                accounts={accounts}
                projects={projects}
              />
            </Grid>
          )}
        </Grid>
        <Navigation open={isMenuOpen} />
      </Box>
      <Box>
        <ListItemButton
          onClick={handleToggleModal}
          sx={{
            pl: 0,
            borderRadius: 1,
            pr: isMenuOpen ? 2.8 : 0,
            mb: 0,
            py: 0,
            justifySelf: isMenuOpen ? 'flex-start' : 'center',
            ml: isMenuOpen ? 1 : 1,
            mr: isMenuOpen ? 0 : 1,
          }}
        >
          <Tooltip
            title={'Send Feedback'}
            placement="right"
            disableHoverListener={isMenuOpen}
          >
            <ListItemIcon sx={{ justifyContent: 'center' }}>
              <MapsUgcOutlinedIcon />
            </ListItemIcon>
          </Tooltip>

          <ListItemText
            primary={'Send Feedback'}
            sx={{
              whiteSpace: 'nowrap',
              opacity: isMenuOpen ? 1 : 0,
              width: isMenuOpen ? 'auto' : 0,
              transition: isMenuOpen
                ? 'opacity 175ms ease-out 200ms, width 150ms ease-out'
                : 'opacity 100ms ease-in, width 100ms ease-in',
            }}
          />
        </ListItemButton>

        {!isMenuOpen && (
          <Stack sx={{ justifySelf: 'center' }}>
            <CrossLinksMenu />
          </Stack>
        )}

        {isModalOpen && (
          <ReportIssueModal
            isModalOpen={isModalOpen}
            handleCloseModal={handleToggleModal}
          />
        )}
      </Box>
    </Drawer>
  );
};

export default Sidebar;
